import { snippetFor } from "@/lib/seo-snippets";
import { activeVariant } from "@/lib/seo-ab-titles";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { PALS, ELEMENT_COLORS, type Element } from "@/lib/pals-data";
import { breadcrumbSchema, toolSchema } from "@/lib/schema";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { PalpagosMap, MapViewArt } from "@/components/MapArt";
import {
  MAP_REGIONS,
  MAP_HOTSPOTS,

  hotspotsForRegion,
  MAP_VIEWS,
  BIOME_COLORS,
  mapGuideSlug,
  type MapView,
} from "@/lib/maps-data";
import { ShareButton } from "@/components/ShareButton";
import { OfflineMapCache } from "@/components/OfflineMapCache";
import { InternalLinkBlock } from "@/components/InternalLinkBlock";
import { ClusterLinks } from "@/components/ClusterLinks";

const FAQ: FaqItem[] = [
  {
    q: "How many maps and regions does Palworld have?",
    a: `Pal Stars charts ${MAP_REGIONS.length} regions of the Palpagos Islands across ${MAP_VIEWS.length} maps — the region overview plus dedicated alpha boss, Syndicate tower, dungeon, resource, fast-travel, Sakurajima, Feybreak, World Tree and Sunreach sky island maps.`,
  },
  {
    q: "Where is the highest level area on the Palworld map?",
    a: "Feybreak, in the far west, is the end-game island at roughly level 50–60. It holds the densest alpha population in Palworld, the Oculus Gate and the only hexolite quartz nodes in the game.",
  },
  {
    q: "Is the World Tree part of the Palpagos Islands map?",
    a: "No. The World Tree is a separate instanced raid map with its own layout and wave-based encounters, so it appears as its own map in the library rather than as a Palpagos region.",
  },
  {
    q: "Which region should I start in?",
    a: "Sea Breeze Archipelago and the Windswept Hills are the level 1–15 bands. Both have flat build sites, dense berry bushes and early ore, and the Windswept Hills holds the first Syndicate Tower fight against Zoe & Grizzbolt.",
  },
  {
    q: "Are these official Palworld maps?",
    a: "No. Pal Stars is an unofficial fan wiki, so every map here is original vector artwork drawn on the real Palpagos region layout rather than a copy of the in-game map, which remains Pocketpair intellectual property.",
  },
  {
    q: "Best farming routes for Pure Quartz Palworld Sakurajima?",
    a: "Use the resource map layer to chain snowy quartz nodes, then route through Sakurajima and Feybreak fast-travel points for ore, coal, sulfur, oil and hexolite quartz checks without doubling back.",
  },
];

const MAP_SNIPPET = snippetFor("/map")!;
// Title/description are under a weekly A/B test (see seo-ab-titles.ts).
const AB = activeVariant("/map");

export const Route = createFileRoute("/map")({
  head: () => ({
    // CTR override: the map ranks for "all fast travel / teleport locations
    // palworld" and "alpha pal map", so the snippet names those layers.
    meta: [
      { title: AB?.title ?? MAP_SNIPPET.title },
      { name: "description", content: AB?.description ?? MAP_SNIPPET.description },
      { property: "og:title", content: MAP_SNIPPET.title },
      { property: "og:description", content: MAP_SNIPPET.description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/map" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: MAP_SNIPPET.title },
      { name: "twitter:description", content: MAP_SNIPPET.description },
      { name: "robots", content: "index, follow, max-snippet:-1, max-image-preview:large" },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/map" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Interactive Map", "/map"]]),
      toolSchema({
        name: "Palworld Interactive Map",
        description: "Interactive Palpagos Islands map of Pal spawn locations, bosses, dungeons and resources by region, element and biome.",
        path: "/map",
      }),
      faqSchema(FAQ, "https://palstars.com/map"),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Palworld map library",
          numberOfItems: MAP_VIEWS.length,
          itemListElement: MAP_VIEWS.map((v, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: v.name,
            url: `https://palstars.com/guides/${mapGuideSlug(v)}`,
          })),
        }),
      },
    ],
  }),
  component: MapPage,
});

const MAP_STATE_KEY = "palstars:map-view";

type RarityFilter = "All" | "Common" | "Uncommon" | "Rare" | "Legendary";

function MapPage() {
  const [view, setView] = useState<MapView>(MAP_VIEWS[0]);
  const [active, setActive] = useState<string>("windswept-hills");

  // Remember the layer + region the visitor was looking at, so opening a boss
  // or map guide and coming back lands them on the same view.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(MAP_STATE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw) as { view?: string; active?: string };
      const v = MAP_VIEWS.find((x) => x.slug === saved.view);
      if (v) setView(v);
      if (saved.active && MAP_REGIONS.some((r) => r.slug === saved.active)) setActive(saved.active);
    } catch {
      /* ignore unreadable session state */
    }
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(MAP_STATE_KEY, JSON.stringify({ view: view.slug, active }));
    } catch {
      /* storage disabled */
    }
  }, [view.slug, active]);
  const [elementFilter, setElementFilter] = useState<Element | "All">("All");
  const [biomeFilter, setBiomeFilter] = useState("All");
  const [rarityFilter, setRarityFilter] = useState<RarityFilter>("All");

  const regions = useMemo(() => {
    const inView = view.focus.length ? MAP_REGIONS.filter((r) => view.focus.includes(r.slug)) : MAP_REGIONS;
    return inView.filter((r) => biomeFilter === "All" || r.biome === biomeFilter);
  }, [view, biomeFilter]);

  const region = useMemo(
    () => regions.find((r) => r.slug === active) ?? regions[0] ?? MAP_REGIONS[0],
    [regions, active],
  );

  const spawns = useMemo(() => {
    if (!region) return [];
    const keys = new Set(region.spawnKeys);
    return PALS.filter(
      (p) =>
        keys.has(p.spawn) &&
        (elementFilter === "All" || p.element === elementFilter) &&
        (rarityFilter === "All" || p.rarity === rarityFilter),
    );
  }, [region, elementFilter, rarityFilter]);

  // Boss pins currently drawn on the map, mirrored as a text list further down
  // the page so keyboard and screen-reader users get the same information.
  const visibleHotspots = useMemo(() => {
    const shown = new Set(regions.map((r) => r.slug));
    return MAP_HOTSPOTS.filter((h) => shown.has(h.regionSlug)).sort(
      (a, b) => a.level - b.level || a.name.localeCompare(b.name),
    );
  }, [regions]);

  const hotspotGroups = useMemo(() => {
    const map = new Map<string, typeof visibleHotspots>();
    for (const h of visibleHotspots) {
      const list = map.get(h.regionName) ?? [];
      list.push(h);
      map.set(h.regionName, list);
    }
    return [...map.entries()];
  }, [visibleHotspots]);



  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
      <h1 className="text-3xl sm:text-4xl font-bold">Palworld Interactive Map</h1>
      <p className="text-muted-foreground mt-2 max-w-3xl">
        The real in-game Palworld world map, with every Palpagos region pinned in place — main island in
        the centre, Ice Wind Island north, the Twilight Dunes desert north-east, Mount Obsidian west,
        Sakurajima north-west, Feybreak in the south-west and the Sunreach sky isles east. Switch map
        layers, then click any region for its level band, bosses, resources and full spawn list.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <ShareButton title="Palworld Interactive Map" text="Every Palpagos region, boss and resource layer on one map." path="/map" />
        <Link to="/guides/$slug" params={{ slug: "palworld-map-library-guide" }} search={{ from: "map" as const }} className="text-sm text-primary hover:underline">
          Read the Palworld map library guide →
        </Link>
        <OfflineMapCache />
      </div>

      {/* Map layer switcher */}
      <div className="mt-6 flex flex-wrap gap-2" role="tablist" aria-label="Map layers">
        {MAP_VIEWS.map((v) => (
          <button
            key={v.slug}
            role="tab"
            aria-selected={v.slug === view.slug}
            onClick={() => {
              setView(v);
              const first = v.focus.length ? v.focus[0] : "windswept-hills";
              setActive(first);
            }}
            className={`rounded-full px-3 py-1.5 text-xs sm:text-sm border transition ${
              v.slug === view.slug ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-card"
            }`}
          >
            {v.name.replace(" Map", "")}
          </button>
        ))}
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <select value={biomeFilter} onChange={(e) => setBiomeFilter(e.target.value)} aria-label="Filter by biome" className="rounded-lg border bg-background/50 px-3 py-2 text-sm">
          <option value="All">All biomes</option>
          {Object.keys(BIOME_COLORS).map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <select value={elementFilter} onChange={(e) => setElementFilter(e.target.value as Element | "All")} aria-label="Filter by element" className="rounded-lg border bg-background/50 px-3 py-2 text-sm">
          <option value="All">All elements</option>
          {(Object.keys(ELEMENT_COLORS) as Element[]).map((el) => <option key={el} value={el}>{el}</option>)}
        </select>
        <select value={rarityFilter} onChange={(e) => setRarityFilter(e.target.value as RarityFilter)} aria-label="Filter by rarity" className="rounded-lg border bg-background/50 px-3 py-2 text-sm">
          {(["All", "Common", "Uncommon", "Rare", "Legendary"] as RarityFilter[]).map((r) => <option key={r} value={r}>{r} rarities</option>)}
        </select>
      </div>

      <div className="grid gap-6 md:grid-cols-[1fr_340px] mt-8">
        <div className="glass rounded-2xl p-3 overflow-hidden">
          <PalpagosMap regions={regions} active={region?.slug ?? null} onSelect={setActive} layer={view.layer} />
          <p className="px-2 pt-2 text-xs text-muted-foreground">{view.name}: {view.short}</p>
          <div className="flex flex-wrap gap-2 mt-3 text-xs">
            <span className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-card">
              <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M5 1 L9 8 H1 Z" fill="currentColor" /></svg>
              Boss hotspot (click to open its guide)
            </span>
            {Object.entries(BIOME_COLORS).map(([b, c]) => (
              <span key={b} className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-card">
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: c }} />{b}
              </span>
            ))}
          </div>
        </div>

        <aside className="glass rounded-2xl p-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Selected region</div>
          <h2 className="text-xl font-bold mt-1">{region?.name ?? "Pick a region"}</h2>
          {region && (
            <>
              <p className="mt-1 text-xs text-muted-foreground">
                {region.biome} · {region.levels}
              </p>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{region.summary}</p>
              <p className="mt-2 text-sm"><span className="font-semibold">Threats:</span> <span className="text-muted-foreground">{region.threats}</span></p>
              <p className="mt-2 text-sm"><span className="font-semibold">Resources:</span> <span className="text-muted-foreground">{region.resources.join(", ")}</span></p>
              <Link
                to="/guides/$slug"
                params={{ slug: mapGuideSlug(view) }}
                search={{ from: "map" as const }}
                className="mt-3 inline-block text-sm text-primary hover:underline"
              >
                {view.name} guide & FAQ →
              </Link>
              {hotspotsForRegion(region.slug).length > 0 && (
                <>
                  <div className="mt-4 text-xs uppercase tracking-wider text-muted-foreground">
                    Bosses here ({hotspotsForRegion(region.slug).length})
                  </div>
                  <div className="mt-2 space-y-1.5">
                    {hotspotsForRegion(region.slug).map((h) => (
                      <Link key={h.bossSlug} to="/guides/$slug" params={{ slug: h.guideSlug }} search={{ from: "map" as const }}
                        className="flex items-center justify-between gap-2 rounded-md px-3 py-2 hover:bg-card">
                        <span className="text-sm font-medium truncate">{h.name}</span>
                        <span className="text-xs text-muted-foreground shrink-0">{h.kind} · Lv {h.level}</span>
                      </Link>
                    ))}
                  </div>
                </>
              )}
              <div className="mt-4 text-xs uppercase tracking-wider text-muted-foreground">
                Spawns ({spawns.length})
              </div>
              <div className="mt-2 space-y-2 max-h-[380px] overflow-auto pr-1">
                {spawns.map((p) => (
                  <Link key={p.id} to="/pals/$slug" params={{ slug: p.slug }}
                    className="flex items-center justify-between gap-2 rounded-md px-3 py-2 hover:bg-card">
                    <span className="text-sm font-medium truncate">{p.name}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: ELEMENT_COLORS[p.element] + "22", color: ELEMENT_COLORS[p.element] }}>{p.element}</span>
                  </Link>
                ))}
                {spawns.length === 0 && <div className="text-sm text-muted-foreground p-3">No Pals match these filters in this region.</div>}
              </div>
            </>
          )}
        </aside>
      </div>

      {/* Accessible, non-visual alternative to the SVG boss pins. Every pin on the
          map above is also reachable here as plain text for keyboard and screen
          reader users, grouped by region and sorted by level. */}
      <section className="glass rounded-2xl p-6 mt-8">
        <h2 className="text-2xl font-bold">Boss hotspots on this map ({visibleHotspots.length})</h2>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          Every boss pin drawn on the map is listed here as text. Pins on the map are focusable with
          Tab and open with Enter; the region markers are buttons you can activate with Enter or Space.
          Use this list if you prefer keyboard, screen reader or small-screen navigation.
        </p>
        <p className="sr-only" aria-live="polite">
          {region ? `${region.name} selected. ${hotspotsForRegion(region.slug).length} bosses in this region.` : ""}
        </p>
        <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {hotspotGroups.map(([regionName, list]) => (
            <div key={regionName}>
              <h3 className="text-sm font-semibold">{regionName}</h3>
              <ul className="mt-2 space-y-1.5">
                {list.map((h) => (
                  <li key={h.bossSlug}>
                    <Link
                      to="/guides/$slug"
                      params={{ slug: h.guideSlug }}
                      search={{ from: "map" as const }}
                      className="flex min-h-11 items-center justify-between gap-2 rounded-md px-3 py-2 text-sm hover:bg-card focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      <span className="truncate font-medium">{h.name}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">{h.kind} · Lv {h.level}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {visibleHotspots.length === 0 && (
            <p className="text-sm text-muted-foreground">
              This map layer has no world boss pins — check the alpha boss or tower map layers.
            </p>
          )}
        </div>
        <Link to="/boss-route" className="mt-5 inline-block text-sm text-primary hover:underline">
          Plan a full boss route from these hotspots →
        </Link>
      </section>



      {/* Map library */}
      <section className="mt-12">
        <h2 className="text-2xl font-bold">Palworld map library ({MAP_VIEWS.length} maps)</h2>
        <p className="text-muted-foreground mt-2 max-w-3xl">
          A map for every layer players actually search for, each with its own guide, boss list and
          FAQ. Click a map to open its guide, or use the layer buttons above to load it in the
          interactive map.
        </p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mt-6">
          {MAP_VIEWS.map((v) => (
            <div key={v.slug} className="glass rounded-xl overflow-hidden card-hover">
              <button
                onClick={() => {
                  setView(v);
                  setActive(v.focus.length ? v.focus[0] : "windswept-hills");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="block w-full text-left"
                aria-label={`Load ${v.name} in the interactive map`}
              >
                <MapViewArt view={v} />
              </button>
              <div className="p-4">
                <h3 className="font-semibold">{v.name}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{v.short}</p>
                <p className="mt-2 text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">Bosses:</span> {v.bosses.join(", ")}
                </p>
                <Link
                  to="/guides/$slug"
                  params={{ slug: mapGuideSlug(v) }}
                  search={{ from: "map" as const }}
                  className="mt-3 inline-block text-sm text-primary hover:underline"
                >
                  Guide & FAQ →
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      <InternalLinkBlock path="/map" />
      <FaqSection items={FAQ} heading="Palworld map FAQ" />
      <ClusterLinks hub="/map" heading="Palworld map guides by location type" />

      <nav aria-label="Related Palworld tools" className="glass rounded-2xl p-6 mt-8">
        <h2 className="text-xl font-bold">Plan the next trip</h2>
        <ul className="mt-3 grid gap-2 sm:grid-cols-2">
          <li><Link to="/bosses" className="text-sm text-primary hover:underline">Palworld boss database</Link></li>
          <li><Link to="/mounts" className="text-sm text-primary hover:underline">Mount & flying speed chart</Link></li>
          <li><Link to="/capture-rates" className="text-sm text-primary hover:underline">Capture rate table</Link></li>
          <li><Link to="/damage" search={{ boss: undefined }} className="text-sm text-primary hover:underline">DPS & damage calculator</Link></li>
          <li><Link to="/elements" className="text-sm text-primary hover:underline">Elemental type chart</Link></li>
          <li><Link to="/guides" className="text-sm text-primary hover:underline">All Palworld guides</Link></li>
        </ul>
      </nav>
    </div>
  );
}
